import type { Place } from "@couples/shared";
import { MapPinCheck, Pencil, RotateCcw, Trash2 } from "lucide-react";

import { formatDateJa } from "../lib/dates";

interface Props {
  place: Place;
  busy: boolean;
  onEdit: () => void;
  onDelete: () => void;
  /** 「行きたい」の1件を「行った」に移す。行った日は今日で確定させ、日付を直したいときは編集から */
  onMarkVisited: () => void;
  /** 「行った」の1件を「行きたい」に戻す(付け間違いの取り消し用) */
  onMarkWant: () => void;
}

/** 共有リストの場所1件の行 */
export default function PlaceCard({
  place,
  busy,
  onEdit,
  onDelete,
  onMarkVisited,
  onMarkWant,
}: Props) {
  return (
    <div className="rounded-xl bg-white p-4 pt-3.5 shadow-sm">
      <div className="mb-1 flex items-start justify-between gap-2">
        <h3 className="text-sm leading-relaxed font-bold">{place.name}</h3>
        {place.status === "visited" && place.visitedOn !== null && (
          <span className="diary-stamp shrink-0">{formatDateJa(place.visitedOn)}</span>
        )}
      </div>

      {place.memo !== null && place.memo !== "" && (
        <p className="mb-3 text-[12.5px] leading-relaxed whitespace-pre-line text-[var(--color-sub)]">
          {place.memo}
        </p>
      )}

      <div className="mt-2 flex items-center justify-end gap-1 border-t border-[var(--color-line)] pt-2.5">
        {place.status === "want" ? (
          <button
            type="button"
            onClick={onMarkVisited}
            disabled={busy}
            className="mr-auto flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-bold text-[var(--color-accent)] disabled:opacity-50"
          >
            <MapPinCheck className="size-3.5" aria-hidden />
            行った
          </button>
        ) : (
          <button
            type="button"
            onClick={onMarkWant}
            disabled={busy}
            className="mr-auto flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-bold text-[var(--color-sub)] disabled:opacity-50"
          >
            <RotateCcw className="size-3.5" aria-hidden />
            行きたいに戻す
          </button>
        )}
        <button
          type="button"
          onClick={onEdit}
          aria-label={`${place.name} を編集`}
          className="-m-1.5 flex size-8 items-center justify-center rounded-full text-[var(--color-sub)] transition-colors hover:bg-[color-mix(in_srgb,var(--color-ink)_7%,transparent)]"
        >
          <Pencil className="size-4" aria-hidden />
        </button>
        <button
          type="button"
          onClick={onDelete}
          disabled={busy}
          aria-label={`${place.name} を削除`}
          className="-m-1.5 flex size-8 items-center justify-center rounded-full text-[var(--color-plum)] transition-colors hover:bg-[color-mix(in_srgb,var(--color-ink)_7%,transparent)] disabled:opacity-50"
        >
          <Trash2 className="size-4" aria-hidden />
        </button>
      </div>
    </div>
  );
}
